"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlanStep = void 0;
const Step_1 = require("../types/Step");
const Utils_1 = require("../Utils");
const ExtrasStep_1 = require("./ExtrasStep");
const FlavorOverrides_1 = require("../FlavorOverrides");
/* Loaded on demand by initCarousel(), not from the page head. Keep the version in step with the
   swiffy-slider stylesheet still linked in order-form2.ejs. */
const SWIFFY_SRC = 'https://cdn.jsdelivr.net/npm/swiffy-slider@1.6.0/dist/js/swiffy-slider.min.js';
/**
 * Plan screen (source design §4.2) — the first half of the old combined
 * ServiceOptionsStep. Owns the offers fetch/render (buildOffers /
 * buildInterestOnly / resetOffers keep their old names — YourInfoStep calls
 * them on steps[1]), plan selection, the #plan-summary continue bar, and every
 * error/edge render (INVALID_ADDRESS etc. all land on THIS screen).
 * Extras (Total Connect / add-ons / promos) live in ExtrasStep.
 */
class PlanStep extends Step_1.Step {
    /**
     * Load swiffy-slider and start the carousel.
     *
     * The script left the page head in the SEO pass (handoff 2026-09-17, §5): the address gate
     * is the first screen and it holds no carousel. It arrives here instead, the first time a
     * carousel actually exists. loadScript() de-dupes, so calling this per rebuild costs nothing.
     *
     * This also closes an old race. The head tag carried `async defer`, so a fast offers fetch
     * could reach initSlider before the script landed, and the call threw.
     *
     * The cards sit in a scroll-snap row, so a failed load costs the nav arrows, not the plans.
     * The matching stylesheet stays in the head — it owns that row's layout.
     */
    initCarousel() {
        (0, Utils_1.loadScript)(SWIFFY_SRC, { crossorigin: 'anonymous' })
            .then(() => window['swiffyslider']?.initSlider(this.sliderContainer.parentElement))
            .catch(e => console.warn(`[OF] carousel script unavailable:`, e));
    }
    constructor(state) {
        super();
        /* state */
        this.autoCardClickHappened = false;
        /* swiffyslider `slideTo` implementation, forcing the scroll to center on item */
        this.slideTo = (sliderElement, slideIndex) => {
            const container = sliderElement.querySelector(".slider-container");
            const gapWidth = parseInt(window.getComputedStyle(container).columnGap);
            const scrollStep = container.children[0].offsetWidth + gapWidth;
            const nodelay = sliderElement.classList.contains("slider-nav-nodelay");
            container.scroll({
                left: (scrollStep * slideIndex) - (scrollStep / 2),
                behavior: nodelay ? "auto" : "smooth"
            });
        };
        this.FORM_STATE = state;
        this.summaryBusinessRow = document.getElementById('summary-company');
        this.loaderContainer = document.getElementById('offers-loader');
        this.errorContainer = document.getElementById('offers-error');
        this.offersHeader = document.getElementById('offers-header');
        this.offersContainer = document.getElementById('offers-container').children[0];
        this.sliderContainer = document.getElementById('offers-slider');
        this.planFeats = document.getElementById('plan-feats');
        this.continueBar = document.getElementById('plan-summary');
        this.continueName = document.getElementById('plan-sum-name');
        this.continuePrice = document.getElementById('plan-sum-price');
        this.continueBtn = document.getElementById('plan-continue');
        this.planWarn = document.getElementById('plan-warn');
        this.planTemplate = document.getElementById('template-plan');
        this.errorTemplate = document.getElementById('template-error');
        this.init();
    }
    init() {
        // continue → gated on a plan being selected, then unlock + advance to Extras
        this.continueBtn.addEventListener('click', (e) => {
            e.preventDefault();
            if (!this.validate()) {
                // source behavior: red pick-a-plan callout, shaken on each blocked continue
                this.planWarn.classList.remove('d-none');
                this.planWarn.classList.remove('shake');
                void this.planWarn.offsetWidth; // restart the animation
                this.planWarn.classList.add('shake');
                return;
            }
            this.advanceToExtras();
        });
        // sp-summary bar: the whole navy bar reads as the CTA (source idiom) — clicks
        // anywhere on it forward to the continue button
        this.continueBar.addEventListener('click', (e) => {
            if (this.continueBtn.disabled) {
                return;
            }
            if (e.target instanceof Node && this.continueBtn.contains(e.target)) {
                return;
            }
            this.continueBtn.click();
        });
        if (this.FORM_STATE.config.devMode) {
            console.info(`[OF] PlanStep loaded.`);
        }
        if (this.FORM_STATE.config.customerHub) {
            // hub embeds start on this step (index 0), so it begins unlocked
            this.unlock();
            let cid = this.FORM_STATE.config.additionalParameters.cid || "";
            // error checks. No token to check: the session is the `auth` cookie, which the browser
            // sends with the same-origin POST in getCurrentOffers. A 401 from that call is the
            // "no access" answer, and it renders its own error state.
            if (!cid || !cid.match(/^\d{9,10}$/)) {
                this.renderError({
                    img: `https://wire3.com/wp-content/uploads/2022/06/wire3-team.png`,
                    title: `Something's not right...`,
                    subtitle: `We're having trouble finding your Customer ID.`,
                    description: `
						<hr class="my-2">
						<p>There may be an issue with your account &mdash; please retry or contact <a target="_blank" href="https://wire3.com/contact-us/">our support</a> for assistance.</p>
					`
                });
                return;
            }
            this.getCurrentOffers(cid);
        }
    }
    onShow() {
        if (this.isFirstShow()) {
            // first show event (name kept from the combined ServiceOptionsStep — funnel continuity)
            (0, Utils_1.sendEvent)('firstShowServiceOptions', this.FORM_STATE);
        }
    }
    validate() {
        return !!this.selectedPlan;
    }
    /** Currently selected offer — ExtrasStep pulls this on late (re)builds. */
    getSelectedPlan() {
        return this.selectedPlan;
    }
    /** Sibling lookup — never by index (step rosters differ between modes). */
    findExtrasStep() {
        return this.FORM_STATE.steps.find(s => s instanceof ExtrasStep_1.ExtrasStep);
    }
    /** Unlock the Extras screen and move to it. */
    advanceToExtras() {
        if (!this.selectedPlan) {
            return;
        }
        // event — the old combined step had no plan-confirm checkpoint; added for funnel granularity
        (0, Utils_1.sendEvent)('confirmedPlan', this.FORM_STATE, { plan: this.selectedPlan.title });
        this.findExtrasStep()?.unlock();
        this.FORM_STATE.frame.stepper.next();
        (0, Utils_1.sendScrollAdjustment)();
    }
    resetOffers() {
        this.selectedPlan = undefined;
        this.FORM_STATE.userInput.selection = undefined;
        // clear out containers
        this.sliderContainer.innerHTML = ``;
        this.offersHeader.innerHTML = ``;
        // show loader + hide offers (+ the static plan-feats strip — it only reads
        // right under rendered plan cards, never over the loader/error states)
        this.loaderContainer.classList.remove('d-none');
        this.offersContainer.parentElement.classList.add('d-none');
        this.planFeats?.classList.add('d-none');
        this.continueBar.classList.add('d-none');
        this.continueBtn.disabled = true;
        this.planWarn.classList.add('d-none');
        this.updateContinueBar();
        // also clear out the Extras screen (addons + promos)
        this.findExtrasStep()?.resetAddonsAndPromos();
    }
    buildOffers() {
        // something went wrong, and we didn't get UGPS data, show error message
        if (!this.FORM_STATE.ugpsData) {
            this.renderError({
                img: `https://wire3.com/wp-content/uploads/2022/06/wire3-team.png`,
                title: `We're Sorry...`,
                subtitle: `There was an issue retrieving your service options.`,
                description: `
					<hr class="my-2">
					<p class="mb-2">We still want to provide you with light speed internet!</p>
					<p>Please contact <a target="_blank" href="https://wire3.com/contact-us/">our support</a> for assistance.</p>
				`
            });
            return;
        }
        // TRANSITIONAL: stamp the hardcoded new-design flavor over known SKUs/addons
        // before anything renders (see FlavorOverrides.ts / docs/ORDER2_FLAVOR.md)
        (0, FlavorOverrides_1.applyFlavorOverrides)(this.FORM_STATE.ugpsData);
        // we got UGPS data, but the address is invalid, show error message
        if (this.FORM_STATE.ugpsData?.code === 'INVALID_ADDRESS') {
            this.renderError({
                img: `https://wire3.com/wp-content/uploads/2022/06/howitworks_illustration.png`,
                title: `Thank You`,
                subtitle: `We don't recognize that address.`,
                description: `
					<p class="mb-2">We were unable to find your address in our systems &ndash; this typically happens with brand-new addresses. Please contact us using one of the methods below, and we'll work towards a solution with you!</p>
					<hr class="my-2">
					<div class="row overflow-hidden py-2 text-center">
            		    <div class="col-12 col-sm-4 col-md-12 col-lg-6 col-xl-4 mb-3 mb-lg-0">
            		        <div class="row align-items-center">
            		            <div class="col-3 col-sm-12 col-md-3 col-lg-12 order-1 order-sm-0 order-md-1 order-lg-0"><img class="mx-auto" src="https://wire3.com/wp-content/uploads/2022/06/icon_newsletter.svg" width="90px" height="100px"></div>
            		            <div class="col-9 col-sm-12 col-md-9 col-lg-12">
            		                <p class="mt-2 mb-1">Email us at:</p>
            		                <p class="m-0"><a class="fw-bold" href="mailto:help@wire3.com">help@wire3.com</a></p>
            		            </div>
            		        </div>
            		    </div>
            		    <div class="col-12 col-sm-4 col-md-12 col-lg-6 col-xl-4 mb-3 mb-lg-0">
            		        <div class="row align-items-center">
            		            <div class="col-3 col-sm-12 col-md-3 col-lg-12 order-1 order-sm-0 order-md-1 order-lg-0"><img class="mx-auto" src="https://wire3.com/wp-content/uploads/2022/06/icon_contact.svg" width="90px" height="100px"></div>
            		            <div class="col-9 col-sm-12 col-md-9 col-lg-12">
            		                <p class="mt-2 mb-1">Text us at:</p>
            		                <p class="m-0"><a class="fw-bold" href="sms:3868778835">(386)-877-8835</a></p>
            		            </div>
            		        </div>
            		    </div>
            		    <div class="col-12 col-sm-4 col-md-12 col-lg col-xl-4 mt-lg-2 mt-xl-0 mb-0">
            		        <div class="row align-items-center">
            		            <div class="col-3 col-sm-12 col-md-3 col-lg-12 order-1 order-sm-0 order-md-1 order-lg-0"><img class="mx-auto" src="https://wire3.com/wp-content/uploads/2022/06/icon_contact-1.svg" width="90px" height="100px"></div>
            		            <div class="col-9 col-sm-12 col-md-9 col-lg-12">
            		                <p class="mt-2 mb-1">Drop us a line at:</p>
            		                <p class="m-0"><a class="fw-bold" href="tel:3868778835">(386)-877-8835</a></p>
            		            </div>
            		        </div>
            		    </div>
                    </div>
				`
            });
            return;
        }
        // we got UGPS data, but no service/offers are available, show error message
        // (abMode is retired on /order2 — the old contact-fields branch is gone)
        if (this.FORM_STATE.ugpsData?.code === 'NO_SERVICE_AREA'
            || this.FORM_STATE.ugpsData?.code === 'NO_OFFERS'
            || !this.FORM_STATE.ugpsData?.offers
            || (this.FORM_STATE.ugpsData.offers?.length === 0)) {
            this.renderError({
                img: `https://wire3.com/wp-content/uploads/2022/06/happy-employees.png`,
                title: `Thank You`,
                subtitle: `We're not currently available in your area.`,
                description: `
					<p>Wire 3 is building our network throughout Volusia, Brevard, Indian River, St. Lucie, Marion, Martin, Osceola, Orange, Lake and Duval counties in Florida.</p>
					<hr class="my-2">
					<p class="mb-2"><strong>If you’re in one of those counties &ndash;</strong> We’ll be in touch when construction starts heading your way. You can even expedite the process by spreading the word and driving up the demand in your area!</p>
					<p><strong>If you’re outside of those counties &ndash;</strong> Right now, we’re not planning to provide service in your area. However, it’s important to note that we are a company driven by our customers. If we’re not currently planning to build in your area, but we get enough interest, you might see a Wire 3 truck heading down your street.</p>
				`
            });
            return;
        }
        let offers = this.FORM_STATE.ugpsData.offers;
        // populate the offer header bar if necessary
        if (this.FORM_STATE.ugpsData?.flavor?.offersHeader) {
            this.offersHeader.innerHTML = this.FORM_STATE.ugpsData.flavor.offersHeader;
        }
        // handle images loading hack
        setTimeout(() => { this.resizePlanRows(); }, 750);
        // if any offers + subsequent addons have the flavor field `learnMore`, we need to load js/css for modals
        if (offers.some(o => o.addons.some(a => !!a.flavor.learnMore))) {
            const bootstrapCSS = document.createElement('link');
            bootstrapCSS.rel = 'stylesheet';
            bootstrapCSS.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css';
            const bootstrapJS = document.createElement('script');
            bootstrapJS.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js';
            // document.head.appendChild(bootstrapCSS);
            document.body.appendChild(bootstrapJS);
        }
        // build plans — sp-card anatomy: gradient name pill + big speed number + whole-dollar
        // price, colored along the source's blue→green ramp by card index
        this.sliderContainer.parentElement.setAttribute('data-slides', String(offers.length));
        const planCount = offers.length;
        for (let [index, plan] of this.FORM_STATE.ugpsData.offers.entries()) {
            // ramp position: slowest plan sits at the blue end, fastest at green (single plan → green)
            const p = planCount === 1 ? 1 : index / (planCount - 1);
            let template = this.planTemplate.innerHTML;
            template = (0, Utils_1.templateReplace)(template, 'i', String(index));
            template = (0, Utils_1.templateReplace)(template, 'PLAN-ID', plan.title);
            template = (0, Utils_1.templateReplace)(template, 'PLAN-IMG', plan.flavor.image ?? '');
            template = (0, Utils_1.templateReplace)(template, 'PLAN-TITLE', plan.flavor.title ?? plan.title);
            template = (0, Utils_1.templateReplace)(template, 'PLAN-CORNER', (plan.flavor.corner ?? '').trim());
            template = (0, Utils_1.templateReplace)(template, 'PLAN-NAME-GRADIENT', `linear-gradient(135deg, ${this.rampColor(p - .05)}, ${this.rampColor(p + .22)})`);
            template = (0, Utils_1.templateReplace)(template, 'PLAN-SPEED-HTML', this.speedHtml(plan.flavor.subtitle ?? '', this.rampText(p)));
            // was-price strikethrough (source .sp-price .was): only when the structured
            // regularPrice actually beats the offer's monthly price — '' hides via :empty
            const showWas = (plan.regularPrice ?? 0) > (plan.monthlyPrice ?? 0);
            template = (0, Utils_1.templateReplace)(template, 'PLAN-WAS-HTML', showWas ? `<span class="was">$${Math.round(plan.regularPrice)}</span>` : '');
            template = (0, Utils_1.templateReplace)(template, 'PLAN-PRICE', String(Math.round(plan.monthlyPrice)));
            template = (0, Utils_1.templateReplace)(template, 'PLAN-SHOWCASE', (plan.flavor.showcase ?? '').trim());
            template = (0, Utils_1.templateReplace)(template, 'PLAN-DESCRIPTION', plan.flavor.description ?? '');
            template = (0, Utils_1.templateReplace)(template, 'PLAN-TOPTEXT', (plan.flavor.topText ?? '').trim());
            // add it to the DOM
            this.sliderContainer.insertAdjacentHTML('beforeend', template);
            // attach plan selection handler
            const planCard = document.querySelector(`label[data-plan-id="${plan.title}"]`);
            planCard.addEventListener('click', (e) => {
                let plan = offers.find(o => o.title === planCard.dataset.planId);
                if (!plan || this.selectedPlan?.internal === plan.internal) {
                    e.preventDefault();
                    return;
                }
                planCard.children[0].checked = true;
                this.slideTo(this.sliderContainer.parentElement, index);
                this.syncDots();
                // change plan
                this.selectedPlan = plan;
                // (re)build the Extras screen for this plan — if the user is already past
                // Plan, the extras rebuild silently and their selections reset (current behavior)
                this.findExtrasStep()?.buildForPlan(plan);
                // sync the continue bar (plan name + $N/mo)
                this.updateContinueBar();
                // show and enable the continue bar
                this.continueBar.classList.remove('d-none');
                this.continueBtn.disabled = false;
                this.planWarn.classList.add('d-none');
                e.preventDefault();
                if (this.autoCardClickHappened) {
                    // event
                    (0, Utils_1.sendGEvent)('select_item', this.FORM_STATE, {
                        item_id: plan.title,
                        item_name: plan.detailedTitle ?? plan.title,
                        price: plan.monthlyPrice,
                        discount: plan.discountAmount,
                        ...(Number(plan.discountAmount > 0) ? { coupon: plan.discountTitle } : {}),
                        index: index,
                        item_category: 'Plan'
                    });
                }
                // keep the plan cards in view (the old scroll-to-TC target now lives on
                // the Extras screen)
                (0, Utils_1.sendScrollAdjustment)(this.offersHeader.offsetHeight);
            });
        }
        // initialize slider
        this.initCarousel();
        // position dots — visible in 1-up (mobile) mode only via CSS; browsing only,
        // the card itself picks (source mobile-audit port)
        this.buildDots(offers);
        // hide loader + show offers, the plan-feats strip and the summary bar in its
        // EMPTY state ("Choose your plan" — nothing is auto-selected, source behavior);
        // Continue stays clickable so a selection-less click lands on the plan-warn path
        this.loaderContainer.classList.add('d-none');
        this.offersContainer.parentElement.classList.remove('d-none');
        this.planFeats?.classList.remove('d-none');
        // optional page-level flavor: UGPS may deliver the plan-feats strip content
        // (raw <li> HTML) — the built-in EJS strip is only the fallback
        if (this.planFeats && this.FORM_STATE.ugpsData?.flavor?.planFeats) {
            this.planFeats.innerHTML = this.FORM_STATE.ugpsData.flavor.planFeats;
        }
        this.continueBar.classList.remove('d-none');
        this.continueBtn.disabled = false;
        this.updateContinueBar();
        // standardize plan description row heights
        this.resizePlanRows();
        // auto-CENTER (never auto-select) the popular/5-Gig card — the source demo
        // centers Orbit on load; fall back along 5-Gig → 2-Gig (SMB) → 1-Gig → last
        const centerIndex = this.autoCenterIndex(offers);
        this.slideTo(this.sliderContainer.parentElement, centerIndex);
        // if rdi is COMMERCIAL, show business name row
        if (this.FORM_STATE.ugpsData.rdi === 'COMMERCIAL') {
            // unhide the business name row
            this.summaryBusinessRow.classList.remove('d-none');
            // make it required for validation pass
            this.summaryBusinessRow.children[1].children[1].required = true;
        }
        if (!this.FORM_STATE.config.autofill) {
            // The flag is raised immediately so every real selection fires its
            // select_item event — the pre-selection below counts, it is the plan the
            // customer came for.
            this.autoCardClickHappened = true;
            // ?promo=FIBER25 pre-picks 250 Mbps, everyone else lands on 5 Gig
            this.autoSelectOnLoad(offers);
        }
        else {
            // autofill: pick the requested plan, then advance — ExtrasStep picks its
            // addons and submits on its own onShow (autofill contract across the split)
            this.autofillSelection();
            if (this.selectedPlan) {
                this.advanceToExtras();
            }
        }
        // event
        (0, Utils_1.sendGEvent)('view_item_list', this.FORM_STATE, {
            items: this.FORM_STATE.ugpsData.offers.map((plan, index) => {
                return {
                    item_id: plan.title,
                    item_name: plan.detailedTitle ?? plan.title,
                    price: plan.monthlyPrice,
                    discount: plan.discountAmount,
                    ...(Number(plan.discountAmount > 0) ? { coupon: plan.discountTitle } : {}),
                    index: index,
                    item_category: 'Plan',
                    item_brand: 'Wire 3',
                    location_id: this.FORM_STATE.userInput.profile?.address
                };
            }) || []
        });
    }
    buildInterestOnly() {
        this.renderError({
            img: `https://wire3.com/wp-content/uploads/2025/08/happy-employees.png`,
            title: `Thank you for your interest!`,
            subtitle: `We hope you'll enjoy what Wire 3 has to offer.`,
            description: ``
        });
        return;
    }
    /**
     * Linear-interpolate between two rgb triples (t clamped to [0,1]).
     * @private
     */
    static lerpRgb(from, to, t) {
        const c = Math.min(1, Math.max(0, t));
        const ch = from.map((v, i) => Math.round(v + (to[i] - v) * c));
        return `rgb(${ch[0]},${ch[1]},${ch[2]})`;
    }
    /**
     * Name-pill/gradient color on the source blue→green speed ramp:
     * lerp(rgb(46,144,224) → rgb(125,196,0)).
     * @private
     */
    rampColor(p) {
        return PlanStep.lerpRgb([46, 144, 224], [125, 196, 0], p);
    }
    /**
     * Accessible speed-number text color on the ramp: lerp(rgb(30,108,190) → rgb(92,146,0)).
     * @private
     */
    rampText(p) {
        return PlanStep.lerpRgb([30, 108, 190], [92, 146, 0], p);
    }
    /**
     * Split a speed string like "1 Gbps" / "250 Mbps" into the big-number treatment
     * (<b>1</b><span>Gbps</span>); unparseable strings render whole in the quiet unit style.
     * @private
     */
    speedHtml(subtitle, color) {
        const trimmed = subtitle.trim();
        const m = trimmed.match(/^([\d.,]+)\s*(.+)$/);
        if (!m) {
            return `<span>${trimmed}</span>`;
        }
        return `<b style="color:${color}">${m[1]}</b><span>${m[2]}</span>`;
    }
    /**
     * Keep the #plan-summary continue bar (plan name + $N/mo) in sync with the
     * current selection.
     * @private
     */
    updateContinueBar() {
        if (!this.selectedPlan) {
            // empty state (nothing auto-selected — source behavior)
            this.continueName.textContent = 'Choose your plan';
            this.continuePrice.textContent = 'Select a plan above to continue';
            return;
        }
        this.continueName.textContent = this.selectedPlan.flavor?.title ?? this.selectedPlan.title;
        this.continuePrice.textContent = `$${Math.round(this.selectedPlan.monthlyPrice || 0)}/mo`;
    }
    /**
     * Card index the carousel should auto-center on load (no selection made):
     * the badged "popular" card when flavor.corner is authored, else the 5-Gig
     * tier, else the 2-Gig SMB tier, else 1-Gig, else the last card.
     * @private
     */
    /** Populate the 1-up position dots and keep them synced to the scroll position. */
    buildDots(offers) {
        const dots = document.getElementById('plan-dots');
        if (!dots) {
            return;
        }
        dots.innerHTML = '';
        offers.forEach((offer, i) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'sp-dot';
            b.setAttribute('aria-label', `Show ${offer.flavor?.title || `plan ${i + 1}`} plan`);
            b.appendChild(document.createElement('i'));
            b.addEventListener('click', () => this.slideTo(this.sliderContainer.parentElement, i));
            dots.appendChild(b);
        });
        // scroll → nearest-card sync (rAF-throttled; smooth slideTo scrolls land here too)
        let raf = 0;
        this.sliderContainer.addEventListener('scroll', () => {
            if (raf) {
                return;
            }
            raf = requestAnimationFrame(() => { raf = 0; this.syncDots(); });
        }, { passive: true });
        this.syncDots();
    }
    /** Reflect the framed card (.on) and the picked plan (.picked) on the dots. */
    syncDots() {
        const dots = document.getElementById('plan-dots');
        const cards = this.sliderContainer.children;
        if (!dots || !dots.children.length || !cards.length) {
            return;
        }
        const gap = parseInt(window.getComputedStyle(this.sliderContainer).columnGap) || 0;
        const step = cards[0].offsetWidth + gap;
        // inverse of slideTo's `left = step*i - step/2`
        const on = Math.max(0, Math.min(dots.children.length - 1, Math.round((this.sliderContainer.scrollLeft + step / 2) / step)));
        const picked = Array.from(cards).findIndex(li => li.querySelector('input[name="plan"]')?.checked);
        Array.from(dots.children).forEach((d, k) => {
            d.classList.toggle('on', k === on);
            d.classList.toggle('picked', picked === k);
        });
    }
    autoCenterIndex(offers) {
        const byCorner = offers.findIndex(o => !!(o.flavor?.corner ?? '').trim());
        if (byCorner >= 0) {
            return byCorner;
        }
        for (const prefix of ['5-Gig', '2-Gig', '1-Gig']) {
            const i = offers.findIndex(o => o.title.startsWith(prefix));
            if (i >= 0) {
                return i;
            }
        }
        return Math.max(0, offers.length - 1);
    }
    /**
     * Legacy plan row-height equalizer — intentionally a NO-OP on order2.
     * Each sp-card is a flex column inside swiffy's stretched grid track, so cards
     * equalize naturally; inline min-heights would fight that layout. The method (and
     * its call sites: buildOffers' image-load hack) are kept so the step's control
     * flow matches order-form-new.
     * @private
     */
    resizePlanRows() { }
    /**
     * Make the UGPS API call for embedded Customer Hub
     *
     * Same-origin, so the `auth` cookie rides along and the body carries no session. The 401 branch
     * below is what "this session cannot see this cid" looks like now.
     *
     * @param cid
     */
    async getCurrentOffers(cid) {
        // event
        (0, Utils_1.sendGEvent)('customer-hub-start_plan_change', this.FORM_STATE);
        // handle API call
        try {
            let res = await fetch((0, Utils_1.apiUrl)('/api/ugpsPlanChange'), {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ "cid": cid })
            });
            if (res.status === 400) {
                throw new Error(`Missing or invalid Customer Hub embed parameters.`);
            }
            else if (res.status === 401) {
                this.renderError({
                    img: `https://wire3.com/wp-content/uploads/2022/06/wire3-team.png`,
                    title: `Somethings not right...`,
                    subtitle: `You don't have access to this account.`,
                    description: `
						<hr class="mt-0 mb-3">
						<p>There may be an issue with your account &mdash; please retry or contact <a target="_blank" href="https://wire3.com/contact-us/">our support</a> for assistance.</p>
					`
                });
                return;
            }
            else if (res.status !== 200) {
                throw new Error(`Unexpected response status: ${res.status}`);
            }
            let data = await res.json();
            if (res.ok && (data?.success != false)) {
                this.FORM_STATE.ugpsData = data;
            }
            else {
                console.warn(`[OF] UGPS ingest error:`, data, res);
                throw new Error('Unsuccessful response from UGPS');
            }
        }
        catch (err) {
            console.warn(`[OF] UGPS submission error:`, err);
        }
        this.buildOffers();
        // hide offers header for customer hub embeds
        this.offersHeader.style.setProperty('display', 'none', 'important');
        (0, Utils_1.sendHeightAdjustment)(this.offersContainer.offsetHeight);
    }
    /**
     * Render an error state in the offers container.
     * Here's a list of images you might wanna use:
     *  - https://wire3.com/wp-content/uploads/2022/06/wire3-team.png
     *  - https://wire3.com/wp-content/uploads/2022/06/howitworks_illustration.png
     *  - https://wire3.com/wp-content/uploads/2025/08/happy-employees.png
     *  - https://wire3.com/wp-content/uploads/2022/06/about.png
     *  - https://wire3.com/wp-content/uploads/2022/06/promise.png
     *  - https://wire3.com/wp-content/uploads/2025/09/Dabby_Rocket_Right-teal-large.png
     *  - https://wire3.com/wp-content/uploads/2025/09/Dabby_Rocket_Left-teal-large.png
     *
     * @param state - The error state details.
     * @private
     */
    renderError(state) {
        let errorHtml = this.errorTemplate.innerHTML;
        errorHtml = (0, Utils_1.templateReplace)(errorHtml, 'ERROR-IMG', state.img);
        errorHtml = (0, Utils_1.templateReplace)(errorHtml, 'ERROR-TITLE', state.title);
        errorHtml = (0, Utils_1.templateReplace)(errorHtml, 'ERROR-SUBTITLE', state.subtitle);
        errorHtml = (0, Utils_1.templateReplace)(errorHtml, 'ERROR-DESCRIPTION', state.description.trim());
        // hide loader + show error container
        this.errorContainer.innerHTML = errorHtml;
        this.loaderContainer.classList.add('d-none');
        this.errorContainer.classList.remove('d-none');
    }
    /**
     * Pre-pick a plan card on load, so the customer lands on a selection instead
     * of an empty carousel.
     *
     * A promo code pointed at one tier wins: FIBER25 prices the 250 Mbps plan, so
     * a customer who follows that campaign link gets the Liftoff card. Everyone
     * else gets 5 Gig (Orbit), the tier the carousel already centers on. A market
     * that carries neither SKU falls back to the plain unselected carousel.
     *
     * The card click drives the whole selection path (Extras build, continue bar,
     * select_item), so the pre-pick goes through the DOM like any other.
     * @private
     */
    autoSelectOnLoad(offers) {
        const isFiber25 = (0, Utils_1.promoCode)(this.FORM_STATE).trim().toUpperCase() === 'FIBER25';
        const plan = (isFiber25 ? PlanStep.find250Plan(offers) : undefined)
            ?? PlanStep.find5GigPlan(offers);
        if (!plan) {
            if (this.FORM_STATE.config.devMode) {
                console.info(`[OF] no 250 Mbps or 5 Gig offer in this market, leaving the plan unselected.`);
            }
            return;
        }
        const planCard = this.sliderContainer.querySelector(`label[data-plan-id="${plan.title}"]`);
        planCard?.click();
    }
    /**
     * The 250 Mbps tier, by SKU prefix (`250-Mb-2025-Regular`) and then by the
     * authored speed subtitle, so a re-priced or renamed 250 SKU still matches.
     * @private
     */
    static find250Plan(offers) {
        return offers.find(o => /^250[-\s]?Mb/i.test(o.title))
            ?? offers.find(o => /^250\s*Mb/i.test((o.flavor?.subtitle ?? '').trim()));
    }
    /**
     * The 5 Gig tier, by SKU prefix (`5-Gig-2025-Regular`) and then by the
     * authored speed subtitle, on the same two-pass rule as {@link find250Plan}.
     * @private
     */
    static find5GigPlan(offers) {
        return offers.find(o => /^5[-\s]?Gig/i.test(o.title))
            ?? offers.find(o => /^5\s*Gig/i.test((o.flavor?.subtitle ?? '').trim()));
    }
    /**
     * Autofill mode (?autofill&offer=..): pick the requested plan card. The click
     * handler builds the Extras screen; ExtrasStep handles its own addon autofill.
     * @private
     */
    autofillSelection() {
        let plan = this.FORM_STATE.config.urlParams.get('offer') || '';
        if (plan) {
            let planCard = this.offersContainer.querySelector(`label[data-plan-id="${plan}"]`);
            if (planCard) {
                planCard.click();
            }
        }
    }
}
exports.PlanStep = PlanStep;
